import React, { useEffect, useMemo, useState } from "react";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import { Link, useSearchParams } from "react-router-dom";
import { Download, Loader2, MapPin, MapPinned, Plus, TriangleAlert, X } from "lucide-react";
import { api, unwrapArray, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ListPager } from "@/components/ui/list-pager";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { ListEmpty, PageHeader, StatusPill, ViewsPanel, initialsOf } from "@/components/patterns";
import { CheckInDialog, CheckOutDialog, PlanVisitDialog, VisitPhotoButton } from "@/components/visits/VisitDialogs";
import { VisitMap, type MapCustomer, type MapPoint } from "@/components/visits/VisitMap";
import { downloadCsv, fetchAllRows } from "@/lib/exportCsv";
import { toQuery } from "@/lib/filters";
import { dateLocale, withClock } from "@/lib/dateTime";
import { cn } from "@/lib/utils";
import { LOCATION_FLAG, VISIT_CHANGED, VISIT_STATUS, customerLink, metres, minutes, useVisitOutcomes, useVisitSettings, type Visit } from "@/lib/visits";

const when = (v: Visit) => v.check_in_at || v.planned_at;
const dayOf = (iso: string | null) => {
  if (!iso) return "—";
  const diff = Math.round((new Date(new Date().toDateString()).getTime() - new Date(new Date(iso).toDateString()).getTime()) / 86400000);
  return diff === 0 ? "Today" : diff === 1 ? "Yesterday" : diff === -1 ? "Tomorrow" : new Date(iso).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" });
};
const timeOf = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString(dateLocale(), withClock({ hour: "numeric", minute: "2-digit" })) : "");
const isoDay = (d: Date) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);

interface Summary {
  owner_id: string;
  name: string;
  visits: number;
  completed: number;
  flagged: number;
  avg_minutes: number | null;
  /** Straight-line kilometres between check-ins, day by day */
  km: number;
}

/** Field visits within your reach: today's, planned, flagged, all of them; a map of one rep's day; and visits per rep */
export function VisitsPage() {
  const { user, hasPermission } = useAuth();
  const { error } = useToast();
  const canCreate = hasPermission("visit.create");
  const settings = useVisitSettings(true);
  const outcomes = useVisitOutcomes();
  const outcomeName = useMemo(() => Object.fromEntries(outcomes.map((o) => [o.value, o.label])), [outcomes]);
  const [params, setParams] = useSearchParams();
  const [view, setView] = useState(params.get("view") || "today");
  const [mode, setMode] = useState<"list" | "map" | "team">("list");
  const [search, setSearch] = useState("");
  const [rep, setRep] = useState("");
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [open, setOpen] = useState<Visit | null>(null);
  const [checkIn, setCheckIn] = useState<{ planned?: Visit | null } | null>(null);
  const [checkOut, setCheckOut] = useState<Visit | null>(null);
  const [planning, setPlanning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [mapDay, setMapDay] = useState(isoDay(new Date()));
  const [mapData, setMapData] = useState<{ points: MapPoint[]; customers: MapCustomer[]; route_km?: number } | null>(null);
  const [summary, setSummary] = useState<Summary[] | null>(null);
  const seesOthers = people.length > 1;

  useEffect(() => {
    api
      .get("/api/User/Assignable?for=lead")
      .then((r) => setPeople(unwrapArray<any>(r).map((u) => ({ id: u.id, name: [u.first_name, u.last_name].filter(Boolean).join(" ") }))))
      .catch(() => undefined);
  }, []);

  const queryString = (extra: Record<string, string | number> = {}) => toQuery({ view, search: search.trim(), owner_id: rep, ...extra });
  const list = usePagedList<Visit>({
    deps: [view, search, rep],
    debounceMs: 250,
    load: async (page) => unwrapPage<Visit>(await api.get(`/api/FieldVisit/List${queryString({ page })}`)),
  });

  // A notification links to /visits?id=…
  useEffect(() => {
    const id = params.get("id");
    if (!id) return;
    api
      .get(`/api/FieldVisit/Get/${id}`)
      .then((r) => setOpen(unwrapObject<Visit>(r)))
      .catch((e) => error("Could not open the visit", e.message || "It may have been removed"));
  }, [params.get("id")]);
  const closeDetail = () => {
    setOpen(null);
    if (params.get("id")) {
      const n = new URLSearchParams(params);
      n.delete("id");
      setParams(n, { replace: true });
    }
  };

  const loadMap = () =>
    api
      .get(`/api/FieldVisit/Map${toQuery({ date: mapDay, owner_id: rep || user?.Id || "" })}`)
      .then((r) => setMapData(unwrapObject(r)))
      .catch((e) => error("Could not load the map", e.message || "Please try again"));
  useEffect(() => {
    if (mode === "map") void loadMap();
  }, [mode, mapDay, rep]);
  useEffect(() => {
    if (mode === "team")
      api
        .get("/api/FieldVisit/Summary")
        .then((r) => setSummary(unwrapArray<Summary>(r)))
        .catch(() => setSummary([]));
  }, [mode]);

  const refresh = () => {
    void list.reload();
    if (mode === "map") void loadMap();
  };
  useEffect(() => {
    window.addEventListener(VISIT_CHANGED, refresh);
    return () => window.removeEventListener(VISIT_CHANGED, refresh);
  });

  const exportAll = async () => {
    setExporting(true);
    try {
      const { rows } = await fetchAllRows<Visit>("/api/FieldVisit/List", queryString());
      downloadCsv<Visit>(`visits-${isoDay(new Date())}.csv`, [
        ["Customer", (r) => r.related_label],
        ["Type", (r) => (r.related_type === "LEAD" ? "Lead" : "Account")],
        ["Deal", (r) => r.deal_label || ""],
        ["Rep", (r) => r.owner?.name || ""],
        ["Status", (r) => VISIT_STATUS[r.visit_status]?.label || r.visit_status],
        ["Planned for", (r) => (r.planned_at ? new Date(r.planned_at).toLocaleString(dateLocale(), withClock()) : "")],
        ["Checked in", (r) => (r.check_in_at ? new Date(r.check_in_at).toLocaleString(dateLocale(), withClock()) : "")],
        ["Checked out", (r) => (r.check_out_at ? new Date(r.check_out_at).toLocaleString(dateLocale(), withClock()) : "")],
        ["Minutes on site", (r) => r.duration_minutes ?? ""],
        ["Metres from customer", (r) => r.check_in_distance_m ?? ""],
        ["Location", (r) => (r.location_flag ? LOCATION_FLAG[r.location_flag].label : "")],
        ["Outcome", (r) => (r.outcome ? outcomeName[r.outcome] || r.outcome : "")],
        ["Notes", (r) => r.notes || ""],
        ["Latitude", (r) => r.check_in_lat ?? ""],
        ["Longitude", (r) => r.check_in_lng ?? ""],
      ], rows);
    } catch (e: any) {
      error("Could not export", e.message || "Please try again");
    } finally {
      setExporting(false);
    }
  };

  if (settings && !settings.enabled) {
    return (
      <div className="space-y-4">
        <PageHeader title="Field visits" />
        <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-[13px] text-muted-foreground">
          Field visits are switched off for your company. A Super Admin can switch them on under Settings → Business rules.
        </p>
      </div>
    );
  }

  const repOptions = [{ value: "", label: seesOthers ? "Everyone" : "Me" }, ...people.map((p) => ({ value: p.id, label: p.id === user?.Id ? `${p.name} (you)` : p.name }))];

  // Filters handed over by Ask the CRM ("Open in Visits")
  const applyAsked = (c: any) => {
    setMode("list");
    setView(c.view || "all");
    setSearch(c.search || "");
    setRep(c.owner || "");
  };

  return (
    <div className="space-y-4">
      <ApplyFromUrl apply={applyAsked} />
      <ViewsPanel
        title="Visits"
        subtitle={seesOthers ? "Your team's visits" : "Your visits"}
        active={mode === "list" ? view : ""}
        onSelect={(v) => {
          setMode("list");
          setView(v);
        }}
        groups={[
          {
            items: [
              { key: "today", label: "Today" },
              { key: "open", label: "On site now", tone: "warning" },
              { key: "planned", label: "Planned" },
              { key: "flagged", label: "Flagged location", tone: "warning" },
              { key: "all", label: "All visits" },
            ],
          },
        ]}
      />

      <PageHeader
        title="Field visits"
        summary={mode === "list" ? list.total : undefined}
        actions={
          <>
            {seesOthers && mode === "list" && (
              <Button variant="outline" size="sm" onClick={exportAll} disabled={exporting}>
                {exporting ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Download className="mr-1.5 size-4" />}
                Export CSV
              </Button>
            )}
            {canCreate && (
              <>
                <Button variant="outline" size="sm" onClick={() => setPlanning(true)}>
                  <Plus className="mr-1.5 size-4" /> Plan visit
                </Button>
                <Button size="sm" onClick={() => setCheckIn({})}>
                  <MapPin className="mr-1.5 size-4" /> Check in
                </Button>
              </>
            )}
          </>
        }
      />
      <p className="text-[13px] text-muted-foreground">
        Check in when you reach a customer and check out when you leave. Check-ins more than {metres(settings?.radius_m ?? 300)} from the customer's saved location are flagged for the manager.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <div role="tablist" aria-label="Show" className="flex gap-1 rounded-lg bg-muted p-0.5">
          {(
            [
              ["list", "List"],
              ["map", "Map"],
              ["team", seesOthers ? "Per rep" : "My numbers"],
            ] as const
          ).map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={mode === k} onClick={() => setMode(k)} className={cn("h-7 rounded-md px-3 text-xs", mode === k ? "bg-card font-semibold shadow-sm" : "text-muted-foreground")}>
              {label}
            </button>
          ))}
        </div>
        {mode === "list" && <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search customer or notes" className="h-8 w-56" />}
        {seesOthers && mode !== "team" && (
          <SearchableSelect value={rep} onChange={(e) => setRep(e.target.value)} className="h-8 w-48 rounded-md border border-input bg-background px-2 text-sm" aria-label="Rep">
            {repOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </SearchableSelect>
        )}
        {mode === "map" && <Input type="date" value={mapDay} max={isoDay(new Date())} onChange={(e) => setMapDay(e.target.value)} className="h-8 w-40" aria-label="Day" />}
      </div>

      {mode === "map" ? (
        <div className="space-y-2">
          {mapData && mapData.points.length === 0 ? (
            <p className="rounded-lg border border-dashed border-border px-4 py-8 text-center text-[13px] text-muted-foreground">No check-ins {seesOthers && rep ? "by this rep " : ""}on this day.</p>
          ) : (
            <VisitMap points={mapData?.points || []} customers={mapData?.customers || []} onSelect={(id) => setOpen(mapData?.points.find((p) => p.id === id) || null)} />
          )}
          {!!mapData?.points.length && (
            <p className="text-[13px]">
              <strong>{mapData.points.length}</strong> check-in{mapData.points.length === 1 ? "" : "s"} · about <strong>{mapData.route_km ?? 0} km</strong> between them in a straight line (the road is longer)
            </p>
          )}
          <p className="text-xs text-muted-foreground">Numbers are check-ins in order; red is far from the customer, amber is weak GPS. Blue squares are where customers are saved.</p>
        </div>
      ) : mode === "team" ? (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border bg-panel hover:bg-panel">
                <TableHead className="pl-3.5">Rep (last 7 days)</TableHead>
                <TableHead className="text-right">Visits</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Average time on site</TableHead>
                <TableHead className="text-right">Flagged</TableHead>
                <TableHead className="text-right" title="Straight-line distance between check-ins, day by day">Km (approx.)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!summary ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-xs text-muted-foreground">Loading…</TableCell>
                </TableRow>
              ) : summary.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-6 text-center text-xs text-muted-foreground">No visits in the last 7 days.</TableCell>
                </TableRow>
              ) : (
                summary.map((s) => (
                  <TableRow key={s.owner_id}>
                    <TableCell className="pl-3.5 font-medium">{s.name}</TableCell>
                    <TableCell className="text-right font-mono">{s.visits}</TableCell>
                    <TableCell className="text-right font-mono">{s.completed}</TableCell>
                    <TableCell className="text-right font-mono">{minutes(s.avg_minutes)}</TableCell>
                    <TableCell className={cn("text-right font-mono", s.flagged > 0 && "text-destructive")}>{s.flagged}</TableCell>
                    <TableCell className="text-right font-mono">{s.km}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      ) : list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={5} />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border bg-panel hover:bg-panel">
                <TableHead className="pl-3.5">When</TableHead>
                <TableHead>Customer</TableHead>
                {seesOthers && <TableHead>Rep</TableHead>}
                <TableHead>Status</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="p-0">
                    <ListEmpty
                      noun="visits"
                      icon={MapPinned}
                      error={list.error}
                      onRetry={list.reload}
                      filtered={!!search.trim() || !!rep || view !== "all"}
                      onClear={() => {
                        setSearch("");
                        setRep("");
                        setView("all");
                      }}
                      firstUse="Check in when you reach a customer: the visit, where you were and how it went are kept here."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                list.rows.map((v) => {
                  const st = VISIT_STATUS[v.visit_status];
                  const fl = v.location_flag ? LOCATION_FLAG[v.location_flag] : null;
                  const mine = v.owner?.id === user?.Id;
                  return (
                    <TableRow key={v.id} onClick={() => setOpen(v)} className="cursor-pointer">
                      <TableCell className="whitespace-nowrap pl-3.5">
                        <span className="block font-medium">{dayOf(when(v))}</span>
                        <span className="text-[11.5px] text-muted-foreground">{timeOf(when(v))}</span>
                      </TableCell>
                      <TableCell>
                        <Link to={customerLink(v)} onClick={(e) => e.stopPropagation()} className="font-semibold text-primary hover:underline">
                          {v.related_label}
                        </Link>
                        <span className="block text-[11.5px] text-muted-foreground">{[v.deal_label ? `Deal: ${v.deal_label}` : v.related_type === "LEAD" ? "Lead" : "Account", v.purpose].filter(Boolean).join(" · ")}</span>
                      </TableCell>
                      {seesOthers && (
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="flex size-[22px] items-center justify-center rounded-full bg-accent text-[9.5px] font-bold text-accent-foreground">{initialsOf(v.owner?.name || "")}</span>
                            {v.owner?.name}
                          </span>
                        </TableCell>
                      )}
                      <TableCell>
                        <StatusPill tone={st.tone}>{st.label}</StatusPill>
                        {v.duration_minutes != null && <span className="ml-1.5 font-mono text-[11.5px] text-muted-foreground">{minutes(v.duration_minutes)}</span>}
                        {v.offline && <span className="ml-1.5 text-[11.5px] text-warning" title="Recorded without signal and sent later; the times are the phone's">· sent later</span>}
                      </TableCell>
                      <TableCell>
                        {fl ? (
                          <span className="inline-flex flex-col">
                            <StatusPill tone={fl.tone}>{fl.label}</StatusPill>
                            {v.check_in_distance_m != null && <span className="text-[11.5px] text-muted-foreground">{metres(v.check_in_distance_m)} away</span>}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {v.outcome ? (
                          <span className="text-[13px]">{outcomeName[v.outcome] || v.outcome}</span>
                        ) : mine && (v.visit_status === "PLANNED" || v.visit_status === "MISSED") && canCreate ? (
                          <Button size="sm" variant="outline" className="h-7" onClick={(e) => { e.stopPropagation(); setCheckIn({ planned: v }); }}>
                            Check in
                          </Button>
                        ) : mine && v.visit_status === "CHECKED_IN" ? (
                          <Button size="sm" className="h-7" onClick={(e) => { e.stopPropagation(); setCheckOut(v); }}>
                            Check out
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />
        </div>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && closeDetail()}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[420px]">
          {open && (
            <VisitDetail
              visit={open}
              outcomeName={outcomeName}
              mine={open.owner?.id === user?.Id}
              onChanged={(v) => {
                setOpen(v);
                refresh();
              }}
              onCheckIn={() => setCheckIn({ planned: open })}
              onCheckOut={() => setCheckOut(open)}
            />
          )}
        </SheetContent>
      </Sheet>

      <CheckInDialog open={!!checkIn} onOpenChange={(o) => !o && setCheckIn(null)} plannedVisit={checkIn?.planned || null} onDone={(v) => { if (open) setOpen(v); refresh(); }} />
      <CheckOutDialog visit={checkOut} onOpenChange={(o) => !o && setCheckOut(null)} onDone={(v) => { if (open) setOpen(v); refresh(); }} />
      <PlanVisitDialog open={planning} onOpenChange={setPlanning} onDone={refresh} />
    </div>
  );
}

const Box = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-1.5 rounded-xl border border-border bg-card px-3.5 py-3">
    <h3 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
    {children}
  </section>
);
const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex justify-between gap-3 text-[13px]">
    <span className="text-muted-foreground">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);
const mapsLink = (lat: number, lng: number) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=18/${lat}/${lng}`;

function VisitDetail({ visit: v, outcomeName, mine, onChanged, onCheckIn, onCheckOut }: { visit: Visit; outcomeName: Record<string, string>; mine: boolean; onChanged: (v: Visit) => void; onCheckIn: () => void; onCheckOut: () => void }) {
  const { success, error } = useToast();
  const st = VISIT_STATUS[v.visit_status];
  const fl = v.location_flag ? LOCATION_FLAG[v.location_flag] : null;
  const [photoKey, setPhotoKey] = useState(0);
  const cancel = async () => {
    try {
      onChanged(unwrapObject<Visit>(await api.post(`/api/FieldVisit/Cancel/${v.id}`)));
      success("Visit cancelled");
    } catch (e: any) {
      error("Could not cancel", e.message || "Please try again");
    }
  };
  return (
    <>
      <SheetHeader className="border-b border-border px-4 py-3.5">
        <SheetTitle className="flex items-center gap-2">
          <Link to={customerLink(v)} className="hover:underline">{v.related_label}</Link>
          <StatusPill tone={st.tone}>{st.label}</StatusPill>
        </SheetTitle>
        <SheetDescription>{[v.owner?.name, v.purpose].filter(Boolean).join(" · ") || "Field visit"}</SheetDescription>
        {v.deal_id && (
          <Link to={`/deals/${v.deal_id}`} className="text-[13px] text-primary hover:underline">
            Deal: {v.deal_label || "open deal"}
          </Link>
        )}
      </SheetHeader>
      <div className="flex flex-col gap-3 p-4">
        {mine && v.can_change && (
          <div className="flex flex-wrap gap-2">
            {(v.visit_status === "PLANNED" || v.visit_status === "MISSED") && (
              <>
                <Button size="sm" onClick={onCheckIn}>
                  <MapPin className="mr-1.5 size-4" /> Check in
                </Button>
                <Button size="sm" variant="outline" onClick={cancel}>
                  <X className="mr-1.5 size-4" /> Cancel visit
                </Button>
              </>
            )}
            {v.visit_status === "CHECKED_IN" && <Button size="sm" onClick={onCheckOut}>Check out</Button>}
            {(v.visit_status === "CHECKED_IN" || v.visit_status === "COMPLETED") && (
              <VisitPhotoButton visitId={v.id} onDone={(nv) => { setPhotoKey((k) => k + 1); onChanged(nv); }} />
            )}
          </div>
        )}
        {!mine && v.can_change && (v.visit_status === "PLANNED" || v.visit_status === "MISSED") && (
          <Button size="sm" variant="outline" className="self-start" onClick={cancel}>
            <X className="mr-1.5 size-4" /> Cancel visit
          </Button>
        )}

        {v.location_flag === "FAR" && (
          <p className="flex gap-2 rounded-md bg-danger-soft px-3 py-2 text-[13px] text-destructive">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" />
            Checked in {metres(v.check_in_distance_m)} from where the customer is saved.
          </p>
        )}

        <Box title="Times">
          {v.planned_at && <Row label="Planned for">{new Date(v.planned_at).toLocaleString(dateLocale(), withClock())}</Row>}
          <Row label="Checked in">{v.check_in_at ? new Date(v.check_in_at).toLocaleString(dateLocale(), withClock()) : "—"}</Row>
          <Row label="Checked out">{v.check_out_at ? new Date(v.check_out_at).toLocaleString(dateLocale(), withClock()) : "—"}</Row>
          <Row label="Time on site">{v.auto_closed ? "Not checked out (closed overnight)" : minutes(v.duration_minutes)}</Row>
          {v.offline && <p className="text-xs text-warning">Recorded without signal and sent later: these times come from the phone.</p>}
        </Box>

        {v.check_in_lat != null && v.check_in_lng != null && (
          <Box title="Location">
            <Row label="Check-in">
              {fl && <StatusPill tone={fl.tone}>{fl.label}</StatusPill>}
            </Row>
            <Row label="From the customer">{metres(v.check_in_distance_m)}</Row>
            <Row label="GPS accuracy">{v.check_in_accuracy_m != null ? `±${metres(v.check_in_accuracy_m)}` : "—"}</Row>
            <Row label="Where">
              <a href={mapsLink(v.check_in_lat, v.check_in_lng)} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                {v.check_in_lat.toFixed(5)}, {v.check_in_lng.toFixed(5)}
              </a>
            </Row>
            {v.check_out_lat != null && v.check_out_lng != null && (
              <Row label="Check-out">
                <a href={mapsLink(v.check_out_lat, v.check_out_lng)} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                  {v.check_out_lat.toFixed(5)}, {v.check_out_lng.toFixed(5)}
                </a>
              </Row>
            )}
          </Box>
        )}

        {(v.outcome || v.notes || v.next_follow_up_at) && (
          <Box title="How it went">
            {v.outcome && <Row label="Outcome">{outcomeName[v.outcome] || v.outcome}</Row>}
            {v.next_follow_up_at && <Row label="Next follow-up">{new Date(v.next_follow_up_at).toLocaleString(dateLocale(), withClock())}</Row>}
            {v.notes && <p className="mt-1 whitespace-pre-wrap rounded-md bg-panel px-2.5 py-2 text-[13px]">{v.notes}</p>}
          </Box>
        )}

        {v.has_photo && (
          <Box title="Photo">
            <a href={`/api/FieldVisit/Photo/${v.id}`} target="_blank" rel="noreferrer">
              <img key={photoKey} src={`/api/FieldVisit/Photo/${v.id}?v=${photoKey}`} alt={`At ${v.related_label}`} loading="lazy" className="max-h-72 w-full rounded-md object-cover" />
            </a>
          </Box>
        )}
      </div>
    </>
  );
}
